import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '../../../components/design-system/admin';
import type { SignupStats } from '../../../common/models/admin-signups.model';
import { useDuration } from './signup-parts';

const STEPS: { key: keyof SignupStats['funnel']; label: string }[] = [
  { key: 'websiteChecked', label: 'WEBSITE_CHECKED' },
  { key: 'accounts', label: 'ACCOUNTS' },
  { key: 'emailVerified', label: 'EMAIL_VERIFIED' },
  { key: 'connected', label: 'CONNECTED' },
  { key: 'approved', label: 'APPROVED' },
  { key: 'onboarded', label: 'ONBOARDED' },
];

/**
 * The last 30 days as one strip: how many reached each step, and what share
 * of the step before. Website checked → Account → Email confirmed →
 * Connected → Approved → Onboarded.
 */
export function SignupFunnel({ stats, loading }: { stats: SignupStats | null; loading?: boolean }) {
  const { t } = useTranslation();
  const duration = useDuration();
  const funnel = stats?.funnel;

  return (
    <Card className="gap-0 p-0" data-testid="signup-funnel">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-4">
        <p className="text-sm font-medium">{t('ADMIN_SIGNUPS.FUNNEL.TITLE')}</p>
        <p className="text-xs text-muted-foreground">
          {stats?.medianReviewMinutes != null
            ? t('ADMIN_SIGNUPS.FUNNEL.MEDIAN_REVIEW', { time: duration(stats.medianReviewMinutes) })
            : t('ADMIN_SIGNUPS.FUNNEL.PERIOD')}
        </p>
      </div>
      <ol className="grid grid-cols-2 gap-px p-4 sm:grid-cols-3 lg:grid-cols-6">
        {STEPS.map((step, i) => {
          const value = funnel?.[step.key] ?? 0;
          const prev = i > 0 ? funnel?.[STEPS[i - 1].key] ?? 0 : null;
          const pct = prev ? Math.round((value / prev) * 100) : null;
          return (
            <li key={step.key} className="relative flex flex-col gap-1 px-3 py-2" data-testid={`funnel-${step.key}`}>
              {i > 0 && <ChevronRight className="absolute start-[-8px] top-3 hidden size-4 text-muted-foreground/50 lg:block rtl:rotate-180" aria-hidden />}
              <span className="text-xs text-muted-foreground">{t(`ADMIN_SIGNUPS.FUNNEL.${step.label}`)}</span>
              {loading && !stats ? (
                <Skeleton className="h-7 w-14" />
              ) : (
                <span className="font-heading text-2xl leading-none font-medium tracking-tight tabular-nums">{formatNumber(value)}</span>
              )}
              <span className="text-xs text-muted-foreground tabular-nums">
                {i === 0 ? t('ADMIN_SIGNUPS.FUNNEL.START') : pct == null ? '—' : t('ADMIN_SIGNUPS.FUNNEL.CONVERSION', { pct })}
              </span>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
